import apiClient from '../config/axios.config';
import { logger } from '../utils/logger';

/**
 * Mirrors RW-ERP-Service/src/api/permissions/users.api.ts → fetchUserbyUserEmail.
 *
 * GET /v1/users/user/email
 *   query:    fromSource=false
 *   headers:  email=<userEmail>
 *
 * The Vue endpoint uses an `email` request HEADER (not a query param) to look
 * up the user. We preserve that contract so the backend route works unchanged.
 */

export interface FetchUserResponseData {
  data: {
    userDetails: {
      userId: string;
      displayName: string;
      email: string;
      [key: string]: unknown;
    };
  };
}

export interface FetchUserResponse {
  status: number;
  data: FetchUserResponseData;
}

export async function fetchUserByEmail(
  emulatedUserEmail: string,
): Promise<FetchUserResponse | { status: number; data?: never }> {
  try {
    const response = await apiClient.get<FetchUserResponseData>(
      '/api/v1/users/user/email',
      {
        params: { fromSource: false },
        headers: { email: emulatedUserEmail },
      },
    );
    return { status: response.status, data: response.data };
  } catch (error) {
    logger.error('fetchUserByEmail failed', error);
    if (error && typeof error === 'object' && 'response' in error) {
      const httpError = error as { response?: { status?: number } };
      return { status: httpError.response?.status ?? 0 };
    }
    return { status: 0 };
  }
}

/**
 * Bulk "all users" fetch — the source that populates the shared
 * `LocalCache.allUserDetails` cache (the same blob the Vue ERP writes).
 *
 *   POST /v1/users/search?fromSource=false
 *   body: an empty composite filter (returns every user)
 *
 * Backend contract (RW-Users-Service `RWUserController#fetchUsersBySearchFilter`
 * → `RWUserService#fetchUsersBySearchFilter`): the response `data` is a map
 * `{ userDetails: { "<userId>": UserDTO }, …roleMaster/teamMaster }`, i.e.
 * the actor map lives at `data.userDetails` keyed by userId. We return the
 * raw response body so the store can cache it in Vue's exact shape.
 *
 * Throws on transport / HTTP error — the caller (`user-details-store`)
 * catches and degrades to the cached value.
 */
const ALL_USERS_SEARCH_ENDPOINT = '/api/v1/users/search';

/** Empty composite filter — the standard RW "fetch all" body. */
const ALL_USERS_SEARCH_BODY = {
  type: 'compositeFilter',
  logicalOperator: 'AND',
  filters: [],
} as const;

export interface AllUsersResponse {
  code?: number;
  status?: boolean;
  message?: string;
  data?: {
    /** Map keyed by userId — the actor lookup the change log resolves against. */
    userDetails?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export async function searchAllUsers(): Promise<AllUsersResponse> {
  const response = await apiClient.post<AllUsersResponse>(
    ALL_USERS_SEARCH_ENDPOINT,
    ALL_USERS_SEARCH_BODY,
    { params: { fromSource: false } },
  );
  return response.data;
}
